import { proxy } from 'valtio'
import { derive, devtools } from 'valtio/utils'
import { type ApiCallType } from '../../types/ApiCallType'
import { resetProxy } from '../../utils/proxyUtils'
import { fieldsRequiredForCardSubmission } from './validators'
import { CardAddFormFieldNamesEnum } from './cardAddFormFieldNamesEnum'

export interface ConvergeType {
  cardBrand: string | null
  cardNumber: string | null
  cardToken: string | null
  cardType: string | null
  expDate: string | null
  sessionToken: string | null
}

export interface VirtualTerminalCardAddProxyType {
  allowSubmitCAP: boolean
  anyLoading: boolean
  apiCallConvergeGetToken: ApiCallType
  apiCallCustomerCardCreate: ApiCallType
  apiCallCustomerAchCreate: ApiCallType
  apiCallCustomerCardDelete: ApiCallType
  apiCallCustomerAchDelete: ApiCallType
  apiCallSessionToken: ApiCallType
  converge: ConvergeType
  displaySlideOverCAP: boolean
  displayConvergeSlideOver: boolean
  displayCardDeleteModal: boolean
  displayAchDeleteModal: boolean
  displayACHSection: boolean
  iframeLoading: boolean
  formValues: { [key in CardAddFormFieldNamesEnum]?: string | null }
  formErrors: { [key in CardAddFormFieldNamesEnum]?: string | null }
  agreementIndicator: boolean
  formDirty: readonly CardAddFormFieldNamesEnum[]
  formBlurred: readonly CardAddFormFieldNamesEnum[]
  showStreetAddressOneError: boolean
  showCityError: boolean
  showStateError: boolean
  showPostalCodeError: boolean
  showFirstNameError: boolean
  showLastNameError: boolean
  showBankAccountError: boolean
  showRoutingNumberError: boolean
  showAccountTypeError: boolean
  showCountryCodeError: boolean
}

const initialValues = {
  agreementIndicator: false,
  apiCallConvergeGetToken: {
    error: null,
    loading: false
  },
  apiCallCustomerAchCreate: {
    error: null,
    loading: false
  },
  apiCallCustomerAchDelete: {
    error: null,
    loading: false
  },
  apiCallCustomerCardCreate: {
    error: null,
    loading: false
  },
  apiCallCustomerCardDelete: {
    error: null,
    loading: false
  },
  apiCallSessionToken: {
    error: null,
    loading: false
  },
  converge: {
    cardBrand: null,
    cardNumber: null,
    cardToken: null,
    cardType: null,
    expDate: null,
    sessionToken: null
  },
  displayAchDeleteModal: false,
  displayACHSection: false,
  displayCardDeleteModal: false,
  displayConvergeSlideOver: false,
  displaySlideOverCAP: false,
  formBlurred: [],
  formDirty: [],
  formErrors: {},
  formValues: {},
  iframeLoading: true
}

const baseProxy = proxy(initialValues)

const showErrorFunctionGenerator = (fieldName: CardAddFormFieldNamesEnum) => (get: any) => {
  const { formDirty, formBlurred, formErrors } = get(baseProxy)
  const isDirty = (formDirty as string[]).includes(fieldName)
  const isBlurred = (formBlurred as string[]).includes(fieldName)
  const isErrored = Boolean((formErrors as { [key in CardAddFormFieldNamesEnum]?: string | null })[fieldName])
  return isDirty && isBlurred && isErrored
}

export const cardAddProxy = derive({
  allowSubmitCAP: (get) => {
    const { formDirty, formErrors, displayACHSection } = get(baseProxy)
    const noErrors = Object.values(formErrors).filter(x => x).length === 0
    const requiredFields = fieldsRequiredForCardSubmission(displayACHSection)
    // @ts-expect-error
    const allDirtied = requiredFields.every((x: string) => formDirty.includes(x))
    return noErrors && allDirtied
  },
  anyLoading: (get) => {
    const { apiCallConvergeGetToken, apiCallCustomerCardCreate, apiCallSessionToken, apiCallCustomerAchCreate } = get(baseProxy)
    return apiCallConvergeGetToken.loading || apiCallCustomerCardCreate.loading || apiCallSessionToken.loading || apiCallCustomerAchCreate.loading
  },
  showAccountTypeError: showErrorFunctionGenerator(CardAddFormFieldNamesEnum.BankAccountType),
  showBankAccountError: showErrorFunctionGenerator(CardAddFormFieldNamesEnum.BankAccountNumber),
  showCityError: showErrorFunctionGenerator(CardAddFormFieldNamesEnum.City),
  showCountryCodeError: showErrorFunctionGenerator(CardAddFormFieldNamesEnum.CountryCode),
  showFirstNameError: showErrorFunctionGenerator(CardAddFormFieldNamesEnum.FirstName),
  showLastNameError: showErrorFunctionGenerator(CardAddFormFieldNamesEnum.LastName),
  showPostalCodeError: showErrorFunctionGenerator(CardAddFormFieldNamesEnum.PostalCode),
  showRoutingNumberError: showErrorFunctionGenerator(CardAddFormFieldNamesEnum.RoutingNumber),
  showStateError: showErrorFunctionGenerator(CardAddFormFieldNamesEnum.State),
  showStreetAddressOneError: showErrorFunctionGenerator(CardAddFormFieldNamesEnum.StreetAddressOne),
  showStreetAddressTwoError: showErrorFunctionGenerator(CardAddFormFieldNamesEnum.StreetAddressTwo)
}, { proxy: baseProxy }) as VirtualTerminalCardAddProxyType

devtools(cardAddProxy, { name: 'cardAddProxy' })

export const resetVirtualTerminalCardAddProxy = (): void => {
  resetProxy(initialValues, cardAddProxy)
}
